import { useEffect, useState } from "react";
import { ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspaces } from "@/lib/useWorkspaces";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CopyContactsDialog = ({ clusters, onDone }) => {
  const { user } = useAuth();
  const { others } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [scope, setScope] = useState("all");
  const [mode, setMode] = useState("copy");
  const [withClusters, setWithClusters] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (others.length && !target) setTarget(others[0].id); }, [others, target]);
  if (user?.role !== "admin" || others.length === 0) return null;

  const run = async () => {
    const dst = others.find((w) => w.id === target);
    const what = scope === "all" ? "tutta la rubrica" : `il cluster «${clusters.find((c) => c.id === scope)?.name}»`;
    if (mode === "move" && !window.confirm(`Spostare ${what} nel canale «${dst?.name}»? I contatti verranno rimossi da questo canale.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/contacts/copy-to", { workspace_id: target, cluster_id: scope === "all" ? null : scope, mode, include_clusters: withClusters });
      const d = r.data;
      toast.success(`${d.created} nuovi contatti in «${d.workspace_name}», ${d.updated} aggiornati, ${d.skipped} già presenti${d.moved ? ` · ${d.moved} rimossi da qui` : ""}`, { duration: 8000 });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="contacts-copy-to-button" className="bg-[#1E293B] border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#334155]">
        <ArrowRightLeft className="h-4 w-4 mr-2" /> Copia in canale
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md" data-testid="copy-contacts-dialog">
          <DialogHeader>
            <DialogTitle>Copia o sposta contatti in un altro canale</DialogTitle>
            <DialogDescription>I numeri già presenti nel canale di destinazione non vengono duplicati (vengono solo aggiunti ai cluster).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Canale di destinazione</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="bg-white border-slate-200" data-testid="copy-contacts-target"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{others.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cosa copiare</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="bg-white border-slate-200" data-testid="copy-contacts-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">Tutta la rubrica</SelectItem>
                  {clusters.map((c) => <SelectItem key={c.id} value={c.id}>Cluster: {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[["copy", "Copia (resta anche qui)"], ["move", "Sposta (rimuovi da qui)"]].map(([v, l]) => (
                <button key={v} onClick={() => setMode(v)} data-testid={`copy-contacts-mode-${v}`}
                        className={`px-3 py-2 rounded-lg text-sm border ${mode === v ? "bg-[#1E4FD8] text-white border-[#1E4FD8]" : "bg-white border-slate-200 text-slate-700"}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm">Ricrea anche i cluster nella destinazione</span>
              <Switch checked={withClusters} onCheckedChange={setWithClusters} data-testid="copy-contacts-clusters" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={run} disabled={busy || !target} className="bg-[#1E4FD8] hover:bg-[#173FB0] text-white" data-testid="copy-contacts-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "move" ? "Sposta" : "Copia"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
